<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="公司账号由企业认证审核通过后自动生成；员工在小程序资料页搜索绑定。此处仅查询与停用。"
    />
    <el-form :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="公司名">
        <el-input
          v-model="queryParams.name"
          clearable
          placeholder="模糊搜索"
          style="width: 200px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态">
        <el-select
          v-model="queryParams.status"
          clearable
          placeholder="全部"
          style="width: 140px"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="Logo" width="72">
        <template #default="{ row }">
          <el-image
            v-if="row.logo"
            :src="row.logo"
            fit="contain"
            style="width: 40px; height: 40px"
            :preview-src-list="[row.logo]"
            preview-teleported
          />
          <span v-else class="text-gray-400">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="公司名称" min-width="160" show-overflow-tooltip />
      <el-table-column prop="creditCode" label="信用代码" min-width="160" show-overflow-tooltip />
      <el-table-column prop="ownerUserId" label="主体用户" width="120" show-overflow-tooltip />
      <el-table-column prop="memberCount" label="成员数" width="90" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.BOSSCIRCLE_COMPANY_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column
        prop="createTime"
        label="创建时间"
        width="180"
        :formatter="dateFormatter"
      />
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['bosscircle:company:update']"
            link
            :type="row.status === 0 ? 'warning' : 'success'"
            @click="toggleStatus(row)"
          >
            {{ row.status === 0 ? '停用' : '启用' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import {
  getCompanyAccountPage,
  updateCompanyAccountStatus,
  type BossCircleCompanyAccountVO
} from '@/api/business/companyAccount'

defineOptions({ name: 'BossCircleCompanyAccount' })

const message = useMessage()
const loading = ref(false)
const list = ref<BossCircleCompanyAccountVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  status: undefined as number | undefined
})

const statusOptions = computed(() => getIntDictOptions(DICT_TYPE.BOSSCIRCLE_COMPANY_STATUS))

const getList = async () => {
  loading.value = true
  try {
    const data = await getCompanyAccountPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.name = undefined
  queryParams.status = undefined
  handleQuery()
}

const toggleStatus = async (row: BossCircleCompanyAccountVO) => {
  if (!row.id && row.id !== 0) return
  const next = row.status === 0 ? 1 : 0
  const tip = next === 1 ? '确认停用该公司账号？员工将无法新绑定。' : '确认重新启用？'
  try {
    await message.confirm(tip)
    await updateCompanyAccountStatus(row.id as string, next)
    message.success('已更新')
    await getList()
  } catch {
    /* cancel */
  }
}

onMounted(() => {
  getList()
})
</script>
